"use client";
import React, {forwardRef, useState} from "react";


import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';

import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from "@/components/ui/textarea";
import {Checkbox} from "@/components/ui/checkbox";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {provinces} from "@/components/FormLead";
import {useRouter} from "next/navigation";
import {Label} from "@/components/ui/label";
import {Button} from "@/components/ui/button";
import {ChevronRight} from "lucide-react";

export interface FromNoleggioProps {
    backgroundImage:string,
    title: string
}


const formSchema = z.object({
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    email: z.string().email({message: 'Inserisci un indirizzo email valido.'}).min(1, {message: 'L\'email è obbligatoria.'}),
    telefono: z.string().min(10, {message: 'Il numero di telefono deve avere almeno 10 cifre.'}).min(1, {message: 'Il telefono è obbligatorio.'}),
    provincia: z.string().min(1, {message: 'La provincia è obbligatoria.'}),
    terms: z.boolean().default(false).refine(val => val, {message: 'Devi accettare i termini e le condizioni.'})
});

const FromNoleggio = forwardRef<HTMLDivElement, FromNoleggioProps>(({
                                                                        backgroundImage,
                                                                        title,
                                                                    }, ref) => {

    const router = useRouter();
    const [loading, setLoading] = useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            nome: '',
            cognome: '',
            email: '',
            telefono: '',
            provincia: '',
            descrizione: '',
            terms: false
        }
    });

    const onSubmit = async (data: any) => {
        setLoading(true);
        // Deconstruisco i dati dal form
        const {nome, email, cognome, telefono, provincia, descrizione} = data;

        // Preparo i dati da inviare nel body della richiesta
        const body = {
            origine: 'Richiesta informazioni',
            argomento: 'LDNL',
            nome: nome ?? '',
            cognome: cognome ?? '',
            telefono: telefono ?? '',
            email: email ?? '',
            provincia: provincia ?? '',
            citta: provincia ? provinces.find((p) => p.code === provincia)?.name : '',
            motivazione: null,
            fonte: sessionStorage.getItem('source_t') || 'WEB',
            articolo: null,
            marca: null,
            modello: null,
            durata: null,
            km: null,
            anticipo: null,
        };

        // Invio la richiesta POST all'API
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/lead`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        // Controllo la risposta
        const result = await response.json();

        console.log('Risultato della richiesta:', result);

        if (response.ok && result.status === 'success') {
            // Reindirizzo alla pagina thank-you con i parametri dinamici
            // pulisco i dati dal form

            if (result.pdf) {
                // Creare un link temporaneo per il download
                const link = document.createElement('a');
                link.href = result.pdf;
                link.target = '_blank';
                link.download = 'preventivo.pdf'; // Imposta il nome del file se necessario
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            const descrizione = title;
            router.push(`/thank-you-page/noleggio?descrizione=${encodeURIComponent(descrizione)}&email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&telefono=${encodeURIComponent(telefono)}`);
            form.reset();
            setLoading(false);

        } else {
            setLoading(false);
            // Gestisci l'errore, ad esempio mostrando un messaggio di errore all'utente
            console.error('Errore durante l\'invio dei dati:', result);
        }

        // Reindirizzo alla pagina thank-you con i parametri dinamici

    };

    return (
        <section ref={ref} id="form-noleggio"
                 className="h-[calc(100svh-100px)] max-sm:h-full max-sm:py-20 text-white"
                 style={{
                     backgroundImage: `url(${backgroundImage})`,
                     backgroundSize: "cover",
                     backgroundPosition: "center",
                     backgroundRepeat: "no-repeat",
                 }}
        >
            <div className="h-full flex items-center">

                <div className="flex flex-col max-w-xl text-center relative mx-auto max-sm:px-6 max-sm:my-8">
                    <h2 className="text-3xl max-sm:text-xl top-[-60px] relative font-bold mb-3">{title}</h2>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)}
                              className="flex flex-col items-center space-y-8 max-sm:mt-[80px]">
                            <FormField control={form.control} name="nome" render={({field, fieldState}) => (
                                <FormItem className="w-full">
                                    <FormControl>
                                        <Input {...field} className="opacity-30 text-black" placeholder="Nome"/>
                                    </FormControl>
                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                </FormItem>
                            )}/>
                            <FormField control={form.control} name="cognome" render={({field, fieldState}) => (
                                <FormItem className="w-full">
                                    <FormControl>
                                        <Input {...field} className="opacity-30 text-black" placeholder="Cognome"/>
                                    </FormControl>
                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                </FormItem>
                            )}/>
                            <FormField control={form.control} name="email" render={({field, fieldState}) => (
                                <FormItem className="w-full">
                                    <FormControl>
                                        <Input {...field} className="opacity-30 text-black" placeholder="Indirizzo Email"/>
                                    </FormControl>
                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                </FormItem>
                            )}/>
                            <FormField control={form.control} name="telefono" render={({field, fieldState}) => (
                                <FormItem className="w-full">
                                    <FormControl>
                                        <Input {...field} className="opacity-30 text-black" placeholder="Numero di telefono"/>
                                    </FormControl>
                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                </FormItem>
                            )}/>
                            <FormField control={form.control} name="provincia"
                                       render={({field, fieldState}) => (
                                           <FormItem className="w-full">
                                               <FormControl>
                                                   <Controller
                                                       control={form.control}
                                                       name="provincia"
                                                       render={({field}) => (
                                                           <Select {...field}
                                                                   onValueChange={(value) => field.onChange(value)}
                                                                   value={field.value}>
                                                               <SelectTrigger
                                                                   className="opacity-30 text-black">
                                                                   <SelectValue
                                                                       placeholder="Seleziona una provincia"/>
                                                               </SelectTrigger>
                                                               <SelectContent>
                                                                   {provinces.map((province) => (
                                                                       <SelectItem
                                                                           key={province.code}
                                                                           value={province.code}>
                                                                           {province.name}
                                                                       </SelectItem>
                                                                   ))}
                                                               </SelectContent>
                                                           </Select>
                                                       )}
                                                   />
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}/>
                            <div className="flex items-center">
                                <FormField control={form.control} name="terms" render={({field, fieldState}) => (
                                    <FormItem>
                                        <FormControl>
                                            <Checkbox checked={field.value} className="text-white"
                                                      onCheckedChange={field.onChange}/>
                                        </FormControl>
                                    </FormItem>
                                )}/>
                                <label htmlFor="terms" className="ml-2 cursor-pointer">
                                    Accetto i <a href="#" className=" underline">termini e
                                    condizioni</a>.
                                </label>
                                {form.formState.errors.terms && (
                                    <div className="text-red-500 text-sm mt-1">
                                        {form.formState.errors.terms.message}
                                    </div>
                                )}
                            </div>
                            <Button className="bg-[#D73133] hover:bg-[#D73133]/90 w-[300px] text-white" type="submit">
                                Invia
                                <ChevronRight className="h-6 w-6 ml-2"/>
                            </Button>
                        </form>
                    </Form>

                </div>
            </div>
        </section>
    )
});

export default FromNoleggio;
